import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Fade,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import StudentDetailModal from "./StudentDetailModal";
import { useNavigate } from "react-router-dom";

const drawerWidth = 240;

function ViewAttendance() {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [streams, setStreams] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStream, setSelectedStream] = useState("");
  const [noStudentsModal, setNoStudentsModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  // Loading states
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);
  const [streamsLoading, setStreamsLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);

  // Select open states for lazy loading
  const [teacherSelectOpen, setTeacherSelectOpen] = useState(false);
  const [classSelectOpen, setClassSelectOpen] = useState(false);
  const [streamSelectOpen, setStreamSelectOpen] = useState(false);

  const token = sessionStorage.getItem("token");
  const navigate = useNavigate();

  const handleOpenModal = (student) => {
    setSelectedStudent(student.studentId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedStudent(null);
    setIsModalOpen(false);
  };

  const handleCloseNoStudentsModal = () => {
    setNoStudentsModal(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fetch Streams
  const fetchStreams = async () => {
    setStreamsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/Stream/GetAllStreams`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ streamID: 0 }),
      });
      const data = await response.json();

      if (Array.isArray(data) && data.length > 0) {
        setStreams(data);
      } else if (data && Array.isArray(data.streams) && data.streams.length > 0) {
        setStreams(data.streams);
      } else {
        setStreams([]);
      }
    } catch (error) {
      console.error("Error fetching streams:", error);
      setStreams([]);
      showSnackbar("Failed to fetch streams.", "error");
    } finally {
      setStreamsLoading(false);
    }
  };

  // Fetch Teachers
  const fetchTeachers = async () => {
    setTeachersLoading(true);
    try {
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/Attendance/GetTeacherList",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setTeachers(data);
      } else if (data && Array.isArray(data.teachers) && data.teachers.length > 0) {
        setTeachers(data.teachers);
      } else {
        setTeachers([]);
      }
    } catch (error) {
      console.error("Error fetching teachers:", error);
      setTeachers([]);
      showSnackbar("Failed to fetch teachers.", "error");
    } finally {
      setTeachersLoading(false);
    }
  };

  // Fetch Classes
  const fetchClasses = async (teacherId) => {
    setClassesLoading(true);
    try {
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/Attendance/GetClass",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ teacherId }),
        }
      );
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setClasses(data);
      } else {
        setClasses([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]);
      showSnackbar("Failed to fetch classes.", "error");
    } finally {
      setClassesLoading(false);
    }
  };

  // Fetch Attendance
  const fetchAttendance = async () => {
    setStudentsLoading(true);
    setStudents([]);

    try {
      const body = {
        stream: selectedStream === "All" ? "0" : selectedStream || "0",
        classId: selectedClass,
        sessionId: 0,
      };

      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/Attendance/GetStudentAdmissionDetails",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(body),
        }
      );

      const data = await response.json();

      if (
        !data ||
        data.length === 0 ||
        data === "null" ||
        data.msg === "Record Not Found"
      ) {
        setNoStudentsModal(true);
        setStudents([]);
        return;
      }

      setStudents(data);
      showSnackbar(
        `Loaded ${data.length} student${data.length > 1 ? "s" : ""}`,
        "success"
      );
    } catch (error) {
      console.error("Error fetching attendance:", error);
      setStudents([]);
      showSnackbar("Failed to fetch students.", "error");
    } finally {
      setStudentsLoading(false);
    }
  };

  useEffect(() => {
    fetchStreams();
  }, []);

  useEffect(() => {
    if (selectedTeacher) {
      fetchClasses(selectedTeacher);
      setSelectedClass("");
      setSelectedStream("");
      setStudents([]);
    } else {
      setClasses([]);
      setSelectedClass("");
      setSelectedStream("");
      setStudents([]);
    }
  }, [selectedTeacher]);

  useEffect(() => {
    if (selectedClass && selectedStream !== undefined) {
      fetchAttendance();
    } else {
      setStudents([]);
    }
  }, [selectedClass, selectedStream]);

  const handleAddAttendance = () => {
    navigate("/attendance-form");
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 7, sm: 8 },
        p: { xs: 2, md: 4 },
        background: "linear-gradient(135deg, #f5f5f5, #ffffff)",
        minHeight: "100vh",
        borderRadius: { xs: 0, md: 2 },
      }}
    >
      <Fade in={true} timeout={500}>
        <Stack spacing={4}>
          {/* Header - Title + Add Button */}
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: 2,
                py: { xs: 2, md: 3 },
                px: 4,
                display: "inline-block",
                minWidth: { xs: "100%", sm: 400 },
                maxWidth: 800,
                mx: "auto",
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  fontSize: { xs: "1.75rem", md: "2rem" },
                }}
              >
                Student Attendance
              </Typography>
            </Box>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 2, mb: 3 }}
            >
              View and manage student attendance records
            </Typography>

            <Button
             
  variant="contained"
  size="large"
  startIcon={<PersonAddIcon />}
  onClick={handleAddAttendance}
sx={{
  minWidth: 250,
  height: 45,
  fontSize: "1.25rem",
  color: "#fff",
  fontWeight: 600,
  background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
  boxShadow: "0 6px 16px rgba(217,119,6,0.35)",
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(145deg, #d97706 0%, #b45309 100%)",
    boxShadow: "0 8px 20px rgba(217,119,6,0.45)",
    transform: "translateY(-2px)",
  },
}}

>
 
            
              Add Attendance
            </Button>
          </Box>

          {/* Filters Card */}
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              transition: "box-shadow 0.3s ease",
              "&:hover": {
                boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
              },
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Grid container spacing={3}>
                {/* Teacher */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
<InputLabel
  sx={{
    fontWeight: 600,
    color: "#d97706",


                        textTransform: "uppercase",
                        fontSize: "0.9rem",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Teacher
                    </InputLabel>
                    <Select
                      value={selectedTeacher}
                      label="Teacher"
                      onChange={(e) => setSelectedTeacher(e.target.value)}
                      open={teacherSelectOpen}
                      onOpen={async () => {
                        setTeacherSelectOpen(true);
                        if (teachers.length === 0 && !teachersLoading) {
                          await fetchTeachers();
                        }
                      }}
                      onClose={() => setTeacherSelectOpen(false)}
                      variant="outlined"
                      sx={{
                        height: 50,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderWidth: 2,
                          borderColor: "#ddd",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d97706",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d97706",
                          boxShadow: "0 0 8px rgba(0,121,107,0.2)",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {teachersLoading ? "Loading teachers..." : "Select Teacher"}
                      </MenuItem>
                      {teachers.map((teacher) => (
                        <MenuItem
                          key={teacher.employeeId ?? teacher.id}
                          value={teacher.employeeId}
                        >
                          {teacher.employeeName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Class */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={!selectedTeacher}>
                    <InputLabel
  sx={{
    fontWeight: 600,
    color: "#d97706",

                        textTransform: "uppercase",
                        fontSize: "0.9rem",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Class
                    </InputLabel>
                    <Select
                      value={selectedClass}
                      label="Class"
                      onChange={(e) => setSelectedClass(e.target.value)}
                      open={classSelectOpen}
                      onOpen={async () => {
                        if (!selectedTeacher) {
                          showSnackbar("Please select a teacher first.", "warning");
                          return;
                        }
                        setClassSelectOpen(true);
                        if (classes.length === 0 && !classesLoading) {
                          await fetchClasses(selectedTeacher);
                        }
                      }}
                      onClose={() => setClassSelectOpen(false)}
                      variant="outlined"
                      sx={{
                        height: 50,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderWidth: 2,
                          borderColor: "#ddd",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d97706",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d97706",
                          boxShadow: "0 0 8px rgba(0,121,107,0.2)",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {classesLoading ? "Loading classes..." : "Select Class"}
                      </MenuItem>
                      {classes.map((cls) => (
                        <MenuItem key={cls.classId} value={cls.classId}>
                          {cls.className}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Stream */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={!selectedClass}>
                    <InputLabel
  sx={{
    fontWeight: 600,
    color: "#d97706",

                        textTransform: "uppercase",
                        fontSize: "0.9rem",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Stream
                    </InputLabel>
                    <Select
                      value={selectedStream}
                      label="Stream"
                      onChange={(e) => setSelectedStream(e.target.value)}
                      open={streamSelectOpen}
                      onOpen={() => setStreamSelectOpen(true)}
                      onClose={() => setStreamSelectOpen(false)}
                      variant="outlined"
                      sx={{
                        height: 50,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderWidth: 2,
                          borderColor: "#ddd",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d97706",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d97706",
                          boxShadow: "0 0 8px rgba(0,121,107,0.2)",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Stream
                      </MenuItem>
                      <MenuItem value="All">All Streams</MenuItem>
                      {streams.map((stream) => (
                        <MenuItem
                          key={stream.streamId ?? stream.id}
                          value={stream.streamName}
                        >
                          {stream.streamName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Results Count */}
          {!studentsLoading && students.length > 0 && (
            <Box sx={{ width: "100%", textAlign: "right" }}>
              <Box
                sx={{
                  display: "inline-block",
                  bgcolor: "white",
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {students.length} student{students.length > 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Table Card */}
          <Card
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <TableContainer
                sx={{
                  "&::-webkit-scrollbar": { height: 8, width: 8 },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "#f1f1f1",
                    borderRadius: "4px",
                  },
                 "&::-webkit-scrollbar-thumb": {
  bgcolor: "#d97706",
},
"&::-webkit-scrollbar-thumb:hover": {
  bgcolor: "#b45309",
},

                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align="center"
                      
  sx={{
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    color: "white",
    fontWeight: 700,

                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Serial No
                      </TableCell>
                   <TableCell
  sx={{
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    color: "white",
    fontWeight: 700,

                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Admission No
                      </TableCell>
                   <TableCell
  sx={{
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    color: "white",
    fontWeight: 700,

                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        First Name
                      </TableCell>
                  <TableCell
  sx={{
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    color: "white",
    fontWeight: 700,

                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Last Name
                      </TableCell>
                      <TableCell
                  
  sx={{
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    color: "white",
    fontWeight: 700,

                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Roll No
                      </TableCell>
                     <TableCell
  sx={{
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    color: "white",
    fontWeight: 700,

                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Stream
                      </TableCell>
                      <TableCell
  sx={{
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    color: "white",
    fontWeight: 700,

                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody
                    sx={{
                      "& tr:nth-of-type(even)": { bgcolor: "#f9f9f9" },
                      "& tr:hover": { bgcolor: "#f1f1f1" },
                    }}
                  >
                    {studentsLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 12 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <CircularProgress sx={{ color: "#d97706" }} size={60} />

                            <Typography variant="body1" sx={{ mt: 3 }}>
                              Loading students...
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : students.length > 0 ? (
                      students.map((student, index) => (
                        <TableRow key={student.id || index} hover>
                          <TableCell align="center">{index + 1}</TableCell>
                          <TableCell>{student.admissionNo}</TableCell>
                          <TableCell>{student.studentName}</TableCell>
                          <TableCell>{student.studentLastName}</TableCell>
                          <TableCell align="center">{student.rollNo}</TableCell>
                          <TableCell>
                            <Chip
                              label={student.stream}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: "#d97706",
                                color: "#d97706",
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleOpenModal(student)}
                            sx={{
  minWidth: 110,
  color: "#fff",
  fontWeight: 600,
  background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
  boxShadow: "0 4px 12px rgba(217,119,6,0.35)",
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(145deg, #d97706 0%, #b45309 100%)",
    boxShadow: "0 6px 16px rgba(217,119,6,0.45)",
    transform: "translateY(-1px)",
  },
}}

                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 12 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="h6" color="text.secondary">
                              No students found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Select a teacher, class, and stream to view students
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Stack>
      </Fade>

      {/* Student Detail Modal */}
      <StudentDetailModal
        isOpen={isModalOpen}
        handleClose={handleCloseModal}
        studentId={selectedStudent}
      />

      {/* No Students Modal */}
      <Modal open={noStudentsModal} onClose={handleCloseNoStudentsModal}>
        <Fade in={noStudentsModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", sm: 500 },
              bgcolor: "background.paper",
              borderRadius: 4,
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              p: { xs: 3, md: 5 },
              textAlign: "center",
            }}
          >
            <IconButton
              onClick={handleCloseNoStudentsModal}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              <CloseIcon />
            </IconButton>

            <PersonAddIcon sx={{ fontSize: 80, color: "#00796b", mb: 3 }} />

            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              No Students Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              There are no students available for the selected filters. Please try different options.
            </Typography>

            <Button
              variant="contained"
              onClick={handleCloseNoStudentsModal}
              sx={{
  minWidth: 140,
  py: 1.5,
  color: "#fff",
  fontWeight: 600,
  background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(145deg, #d97706 0%, #b45309 100%)",
    transform: "translateY(-1px)",
  },
}}

            >
              Close
            </Button>
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ViewAttendance;